/**
 * The generated balance tables, typed and indexed.
 *
 * `data/*.json` is produced by @empire/balance-tools from 02_Balance.xlsx and is
 * never hand-edited. This module is the only place the simulation reads it, so
 * a change in the spreadsheet's shape breaks in one file rather than fifty.
 */
import unitsData from "./data/units.json" with { type: "json" };
import towersData from "./data/towers.json" with { type: "json" };
import tilesData from "./data/tiles.json" with { type: "json" };
import defenceBudgetData from "./data/defenceBudget.json" with { type: "json" };

import type {
  DefenceBudgetRow,
  TileStats,
  TowerKind,
  TowerStats,
  UnitKind,
  UnitStats,
} from "./types.js";

export const UNITS = unitsData.units as readonly UnitStats[];
export const TOWERS = towersData.towers as readonly TowerStats[];
export const TILES = tilesData.tiles as readonly TileStats[];
export const DEFENCE_BUDGET = defenceBudgetData.byWallLevel as readonly DefenceBudgetRow[];

function index<T extends { id: string }>(rows: readonly T[]): ReadonlyMap<string, T> {
  return new Map(rows.map((row) => [row.id, row]));
}

const unitById = index(UNITS);
const towerById = index(TOWERS);
const tileById = index(TILES);

export function unitStats(kind: UnitKind): UnitStats {
  const stats = unitById.get(kind);
  if (!stats) throw new Error(`No balance data for unit "${kind}". Run \`pnpm balance\`.`);
  return stats;
}

/** Level is 1..3; the returned stats already include that level's damage and HP. */
export function towerStats(kind: TowerKind, level: number): TowerStats {
  const stats = towerById.get(kind);
  if (!stats) throw new Error(`No balance data for tower "${kind}". Run \`pnpm balance\`.`);
  if (level < 1 || level > stats.levels.length) {
    throw new Error(`Tower "${kind}" has no level ${level}.`);
  }
  return stats;
}

export function tileStats(id: string): TileStats {
  const stats = tileById.get(id);
  if (!stats) throw new Error(`No balance data for tile "${id}". Run \`pnpm balance\`.`);
  return stats;
}

/** Road, mud and barricade allowances for a Wall level, clamped at the Wall cap. */
export function defenceBudget(wallLevel: number): DefenceBudgetRow {
  const last = DEFENCE_BUDGET[DEFENCE_BUDGET.length - 1];
  if (!last) throw new Error("Defence budget table is empty. Run `pnpm balance`.");
  const row = DEFENCE_BUDGET.find((r) => r.wallLevel === wallLevel);
  return row ?? last;
}
